<template>
  <section class="mv-table-card">
    <div v-if="isLoading" class="state-block">
      <div v-for="n in 3" :key="n" class="row-skeleton" />
    </div>

    <div v-else-if="!modelVersionRecords.length" class="state-block empty-state">
      <v-icon size="40" color="#4a5a60">mdi-source-branch</v-icon>
      <p class="empty-title">No model versions yet</p>
      <p class="empty-subtitle">Create a version to combine one or more processed scans.</p>
    </div>

    <template v-else>
      <!-- Desktop table -->
      <div v-if="!isCompact" class="table-wrap">
        <table class="mv-table">
          <thead>
            <tr>
              <th class="c-ver">Version</th>
              <th class="c-name">Name &amp; description</th>
              <th class="c-scans">Scans</th>
              <th class="c-status">Status</th>
              <th class="c-act"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in modelVersionRecords" :key="item.id" :class="{ 'is-current': item.isCurrent }">
              <td class="c-ver"><span class="version-chip">V{{ item.versionNumber }}</span></td>

              <td class="c-name">
                <div class="name-main">{{ item.versionName }}</div>
                <div class="name-desc" :class="{ muted: !item.description }">{{ item.description || 'No description' }}</div>
              </td>

              <td class="c-scans">
                <div v-if="scansForVersion(item).length" class="scan-chip-row">
                  <span v-for="scan in scansForVersion(item)" :key="scan.scanId" class="scan-chip"
                    :class="{ 'is-primary': scan.isPrimary }" :title="scan.isPrimary ? `${scan.name} (primary)` : scan.name">
                    <span class="scan-dot" :class="{ primary: scan.isPrimary }"></span>{{ scan.name }}
                  </span>
                </div>
                <span v-else class="muted">&mdash;</span>
              </td>

              <td class="c-status">
                <div class="status-wrap">
                  <span v-if="item.isCurrent" class="pill pill-current">
                    <v-icon size="13" color="#4ade80">mdi-check</v-icon>Current
                  </span>
                  <span class="pill" :class="publishStatus(item).cls">
                    <v-icon size="12">{{ publishStatus(item).icon }}</v-icon>{{ publishStatus(item).label }}
                  </span>
                </div>
              </td>

              <td class="c-act">
                <div class="act-wrap">
                  <button v-if="isDesktop" type="button" class="manage-btn" @click="manageModel(item)">Manage model</button>
                  <v-menu location="bottom end" offset="4">
                    <template #activator="{ props }">
                      <button v-bind="props" type="button" class="kebab" aria-label="More actions">
                        <v-icon size="20">mdi-dots-horizontal</v-icon>
                      </button>
                    </template>
                    <v-list class="act-menu" density="compact">
                      <v-list-item v-if="!item.isCurrent" @click="confirmSetCurrent(item)">
                        <template #prepend><v-icon size="18" color="#4ade80">mdi-check-circle-outline</v-icon></template>
                        <v-list-item-title>Set as current</v-list-item-title>
                      </v-list-item>
                      <v-list-item @click="openDuplicate(item)">
                        <template #prepend><v-icon size="18" color="#cfd8dc">mdi-content-copy</v-icon></template>
                        <v-list-item-title>Duplicate</v-list-item-title>
                      </v-list-item>
                      <v-list-item :disabled="item.isCurrent" class="danger-item"
                        :title="item.isCurrent ? 'Set another version as current before deleting this one.' : ''"
                        @click="confirmDelete(item)">
                        <template #prepend><v-icon size="18" :color="item.isCurrent ? '#5a6970' : '#f87171'">mdi-delete-outline</v-icon></template>
                        <v-list-item-title>Delete</v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Mobile card list -->
      <div v-else class="card-list">
        <article v-for="item in modelVersionRecords" :key="item.id" class="version-card"
          :class="{ 'is-current': item.isCurrent }">
          <header class="version-card-head">
            <span class="version-chip">V{{ item.versionNumber }}</span>
            <div class="status-wrap">
              <span v-if="item.isCurrent" class="pill pill-current">
                <v-icon size="13" color="#4ade80">mdi-check</v-icon>Current
              </span>
              <span class="pill" :class="publishStatus(item).cls">
                <v-icon size="12">{{ publishStatus(item).icon }}</v-icon>{{ publishStatus(item).label }}
              </span>
            </div>
          </header>

          <div class="version-card-name">{{ item.versionName }}</div>
          <p class="version-card-desc" :class="{ muted: !item.description }">{{ item.description || 'No description' }}</p>

          <div v-if="scansForVersion(item).length" class="scan-chip-row card-scans">
            <span v-for="scan in scansForVersion(item)" :key="scan.scanId" class="scan-chip"
              :class="{ 'is-primary': scan.isPrimary }">
              <span class="scan-dot" :class="{ primary: scan.isPrimary }"></span>{{ scan.name }}
            </span>
          </div>

          <div class="version-card-actions">
            <button v-if="isDesktop" type="button" class="manage-btn" @click="manageModel(item)">Manage model</button>
            <v-menu location="bottom end" offset="4">
              <template #activator="{ props }">
                <button v-bind="props" type="button" class="kebab" aria-label="More actions">
                  <v-icon size="20">mdi-dots-horizontal</v-icon>
                </button>
              </template>
              <v-list class="act-menu" density="compact">
                <v-list-item v-if="!item.isCurrent" @click="confirmSetCurrent(item)">
                  <template #prepend><v-icon size="18" color="#4ade80">mdi-check-circle-outline</v-icon></template>
                  <v-list-item-title>Set as current</v-list-item-title>
                </v-list-item>
                <v-list-item @click="openDuplicate(item)">
                  <template #prepend><v-icon size="18" color="#cfd8dc">mdi-content-copy</v-icon></template>
                  <v-list-item-title>Duplicate</v-list-item-title>
                </v-list-item>
                <v-list-item :disabled="item.isCurrent" class="danger-item" @click="confirmDelete(item)">
                  <template #prepend><v-icon size="18" :color="item.isCurrent ? '#5a6970' : '#f87171'">mdi-delete-outline</v-icon></template>
                  <v-list-item-title>Delete</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>
        </article>
      </div>
    </template>
  </section>

  <OverlayWindow :isVisible="isDuplicateOverlayVisible" overlayTitle="Duplicate Version" :centralizeWindow="true"
    @closeOverlay="isDuplicateOverlayVisible = false">
    <DuplicateVersionDialog v-if="isDuplicateOverlayVisible" :sourceVersion="duplicateSource" :assetId="assetId"
      @cancel="isDuplicateOverlayVisible = false" @created="onDuplicateCreated" />
  </OverlayWindow>

  <ConfirmDialog v-model="showSetCurrentDialog" title="Set current version?" :message="setCurrentMessage"
    confirmText="Yes, set current" icon="mdi-check-circle-outline" @confirm="onSetCurrentConfirmed" />

  <ConfirmDialog v-model="showDeleteDialog" title="Delete version?" :message="deleteMessage" danger
    confirmText="Yes, delete" icon="mdi-delete-outline" @confirm="onDeleteConfirmed" />
</template>

<script>
import { useDisplay } from 'vuetify';
import { useDeviceCapability } from '@/composable/useDeviceCapability';
import ModelVersionService from '@/services/ModelVersionService';
import OverlayWindow from '../OverlayWindow.vue';
import DuplicateVersionDialog from '../dialog/DuplicateVersionDialog.vue';
import ConfirmDialog from '../dialog/ConfirmDialog.vue';

export default {
  name: 'ModelVersionTable',
  components: { OverlayWindow, DuplicateVersionDialog, ConfirmDialog },
  props: {
    assetId: { type: String, default: null },
  },
  emits: ['changed'],
  setup() {
    const { smAndDown } = useDisplay();
    // isCompact (sm and down) drives the card-vs-table layout switch (a width concern). isDesktop
    // gates the "Manage model" (scan-alignment) action on INPUT CAPABILITY — a precise pointer with
    // hover — so touch phones/tablets are excluded regardless of resolution (dragging the 3D gizmo).
    const { isDesktopPointer } = useDeviceCapability();
    return { isCompact: smAndDown, isDesktop: isDesktopPointer };
  },
  data() {
    return {
      isLoading: true,
      modelVersionRecords: [],
      scanNameById: new Map(),
      showSetCurrentDialog: false,
      pendingVersion: null,
      showDeleteDialog: false,
      pendingDeleteVersion: null,
      isDuplicateOverlayVisible: false,
      duplicateSource: null,
    };
  },
  computed: {
    setCurrentMessage() {
      if (!this.pendingVersion) return '';
      return `Are you sure you want to set "${this.pendingVersion.versionName}" (V${this.pendingVersion.versionNumber}) as the current version? This will replace the existing current version.`;
    },
    deleteMessage() {
      if (!this.pendingDeleteVersion) return '';
      return `Are you sure you want to delete "${this.pendingDeleteVersion.versionName}" (V${this.pendingDeleteVersion.versionNumber})? It will be removed from the version list but can be restored by support if needed.`;
    },
  },
  async mounted() {
    await this.getVersionByAssetId(this.assetId);
  },
  methods: {
    async getVersionByAssetId(assetId) {
      try {
        const [versions, scans] = await Promise.all([
          ModelVersionService.getModelVersionsByAssetId(assetId),
          ModelVersionService.getScansByAssetId(assetId),
        ]);
        this.modelVersionRecords = versions;
        this.scanNameById = new Map((scans ?? []).map(s => [s.id, s.name]));
      } finally {
        this.isLoading = false;
      }
    },
    // Per-row scan list: primary first (flagged), then supplementaries, falling back to the combined
    // `scans` projection when the legacy primary+supplements pair isn't populated.
    scansForVersion(version) {
      if (!version) return [];
      const seen = new Set();
      const out = [];
      const push = (scanId, isPrimary) => {
        if (!scanId || seen.has(scanId)) return;
        seen.add(scanId);
        out.push({ scanId, isPrimary, name: this.scanNameById.get(scanId) ?? 'Unknown scan' });
      };
      push(version.associatedScan, true);
      for (const s of (version.supplementaryScans ?? [])) push(s.scanId, false);
      for (const s of (version.scans ?? [])) push(s.scanId, s.scanId === version.associatedScan);
      return out;
    },
    manageModel(item) {
      this.$router.push({ name: 'ModelVersionScans', params: { id: this.assetId, versionId: item.id } });
    },
    // Publish-state badge. isBaking wins over needsPublish — a version mid-bake is "Processing".
    publishStatus(item) {
      if (item.isBaking) return { label: 'Processing', cls: 'status-processing', icon: 'mdi-progress-clock' };
      if (item.needsPublish) return { label: 'Unpublished', cls: 'status-unpublished', icon: 'mdi-circle-edit-outline' };
      return { label: 'Published', cls: 'status-published', icon: 'mdi-cloud-check-outline' };
    },
    confirmSetCurrent(item) {
      if (item.isCurrent) return;
      this.pendingVersion = item;
      this.showSetCurrentDialog = true;
    },
    onSetCurrentConfirmed() {
      const item = this.pendingVersion;
      this.pendingVersion = null;
      if (item) this.setAsCurrent(item);
    },
    async setAsCurrent(item) {
      if (item.isCurrent) return;
      this.isLoading = true;
      try {
        await ModelVersionService.setCurrentModelVersion(this.assetId, item.id);
      } catch (error) {
        if (process.env.NODE_ENV === 'development') console.error('Failed to set current model version', error);
      } finally {
        await this.getVersionByAssetId(this.assetId);
        this.$emit('changed');
      }
    },
    async refreshTable() {
      this.isLoading = true;
      await this.getVersionByAssetId(this.assetId);
      this.$emit('changed');
    },
    confirmDelete(item) {
      // The current version is never deletable here — that would leave the asset without a current
      // version. Switch current first, then delete the old one.
      if (item.isCurrent) return;
      this.pendingDeleteVersion = item;
      this.showDeleteDialog = true;
    },
    onDeleteConfirmed() {
      const item = this.pendingDeleteVersion;
      this.pendingDeleteVersion = null;
      if (item) this.deleteVersion(item);
    },
    async deleteVersion(item) {
      if (item.isCurrent) return;
      this.isLoading = true;
      try {
        await ModelVersionService.deleteModelVersion(this.assetId, item.id);
      } catch (error) {
        if (process.env.NODE_ENV === 'development') console.error('Failed to delete model version', error);
      } finally {
        await this.getVersionByAssetId(this.assetId);
        this.$emit('changed');
      }
    },
    openDuplicate(item) {
      this.duplicateSource = item;
      this.isDuplicateOverlayVisible = true;
    },
    async onDuplicateCreated() {
      this.isDuplicateOverlayVisible = false;
      this.duplicateSource = null;
      await this.refreshTable();
    },
  },
};
</script>

<style scoped>
.mv-table-card {
  background: rgba(0, 0, 0, 0.22);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 12px;
  overflow: hidden;
}

/* Loading / empty */
.state-block { padding: 18px; display: flex; flex-direction: column; gap: 10px; }
.row-skeleton {
  height: 56px; border-radius: 8px;
  background: linear-gradient(90deg, rgba(255,255,255,0.02) 0%, rgba(255,255,255,0.05) 50%, rgba(255,255,255,0.02) 100%);
  background-size: 200% 100%; animation: skel-shimmer 1.4s ease-in-out infinite;
}
@keyframes skel-shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
.empty-state { align-items: center; text-align: center; padding: 44px 16px; color: #8aa0aa; }
.empty-title { font-size: 1.05rem; font-weight: 600; color: #cfd8dc; margin: 12px 0 4px; }
.empty-subtitle { font-size: 0.88rem; margin: 0; }

/* Table */
.table-wrap { overflow-x: auto; }
.mv-table { width: 100%; border-collapse: collapse; color: #cfd8dc; }
.mv-table thead th {
  font-size: 0.72rem; text-transform: uppercase; letter-spacing: 0.06em; color: #8aa0aa;
  font-weight: 600; text-align: left; padding: 13px 16px;
  background: rgba(0, 0, 0, 0.18); border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}
.mv-table tbody tr { transition: background 0.15s ease; border-bottom: 1px solid rgba(255, 255, 255, 0.04); }
.mv-table tbody tr:last-child { border-bottom: none; }
.mv-table tbody tr:hover { background: rgba(100, 255, 218, 0.04); }
.mv-table tbody tr.is-current { background: rgba(74, 222, 128, 0.04); }
.mv-table td { padding: 14px 16px; font-size: 0.92rem; vertical-align: middle; }

.c-ver { width: 78px; white-space: nowrap; }
.c-name { min-width: 220px; }
.c-scans { min-width: 200px; max-width: 340px; }
.c-status { width: 190px; white-space: nowrap; }
.c-act { width: 200px; text-align: right; white-space: nowrap; }

.name-main { color: #fff; font-weight: 700; }
.name-desc { font-size: 0.82rem; color: #b0bec5; margin-top: 2px; }
.name-desc.muted { color: #667a83; font-style: italic; }

/* Scan chips */
.scan-chip-row { display: flex; flex-wrap: wrap; gap: 6px; }
.scan-chip {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 3px 10px; border-radius: 999px;
  background: rgba(255, 255, 255, 0.04); border: 1px solid rgba(255, 255, 255, 0.08);
  color: #cfd8dc; font-size: 0.76rem; font-weight: 500; white-space: nowrap;
}
.scan-chip.is-primary { background: rgba(74, 222, 128, 0.1); border-color: rgba(74, 222, 128, 0.3); color: #a7f3c0; font-weight: 600; }
.scan-dot { width: 6px; height: 6px; border-radius: 50%; background: #7f939d; flex: 0 0 auto; }
.scan-dot.primary { background: #4ade80; }
.card-scans { margin-top: 2px; }

/* Status pills */
.status-wrap { display: inline-flex; align-items: center; gap: 6px; flex-wrap: wrap; }
.pill {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 3px 9px; border-radius: 999px; font-size: 0.75rem; font-weight: 600; white-space: nowrap;
}
.pill-current { background: rgba(74, 222, 128, 0.12); border: 1px solid rgba(74, 222, 128, 0.35); color: #4ade80; }
.status-published { background: rgba(100, 255, 218, 0.1); border: 1px solid rgba(100, 255, 218, 0.3); color: var(--duallo-accent); }
.status-unpublished { background: rgba(250, 204, 21, 0.1); border: 1px solid rgba(250, 204, 21, 0.35); color: #facc15; }
.status-processing { background: rgba(96, 165, 250, 0.1); border: 1px solid rgba(96, 165, 250, 0.35); color: #60a5fa; }

/* Version chip */
.version-chip {
  display: inline-block; padding: 4px 10px; border-radius: 8px;
  background: rgba(100, 255, 218, 0.1); border: 1px solid rgba(100, 255, 218, 0.25);
  color: var(--duallo-accent); font-size: 0.8rem; font-weight: 700; font-variant-numeric: tabular-nums;
}
.muted { color: #4a5a60; }

/* Actions */
.act-wrap { display: inline-flex; align-items: center; gap: 8px; justify-content: flex-end; }
.manage-btn {
  padding: 8px 16px; border-radius: 9px;
  background: rgba(255, 255, 255, 0.06); border: 1px solid rgba(255, 255, 255, 0.16);
  color: #fff; font: inherit; font-size: 0.85rem; font-weight: 600; cursor: pointer;
  transition: background 0.15s ease;
}
.manage-btn:hover { background: rgba(255, 255, 255, 0.12); }
.kebab {
  display: inline-flex; align-items: center; justify-content: center;
  width: 34px; height: 34px; border-radius: 9px;
  background: transparent; border: 1px solid rgba(255, 255, 255, 0.1); color: #b0bec5; cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}
.kebab:hover { background: rgba(255, 255, 255, 0.08); color: #fff; }

.act-menu { background: #1f2c34 !important; border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 10px; }
.act-menu :deep(.v-list-item-title) { font-size: 0.88rem; }
.danger-item :deep(.v-list-item-title) { color: #ff9c9c; }

/* Mobile cards */
.card-list { display: flex; flex-direction: column; gap: 10px; padding: 14px; }
.version-card {
  display: flex; flex-direction: column; gap: 8px; padding: 14px 16px;
  background: rgba(255, 255, 255, 0.03); border: 1px solid rgba(255, 255, 255, 0.05); border-radius: 12px;
}
.version-card.is-current { background: rgba(74, 222, 128, 0.05); border-color: rgba(74, 222, 128, 0.2); }
.version-card-head { display: flex; align-items: center; justify-content: space-between; gap: 10px; flex-wrap: wrap; }
.version-card-name { font-size: 1rem; font-weight: 700; color: #fff; line-height: 1.3; word-break: break-word; }
.version-card-desc { font-size: 0.86rem; color: #b0bec5; margin: 0; line-height: 1.45; }
.version-card-desc.muted { color: #667a83; font-style: italic; }
.version-card-actions { display: flex; align-items: center; justify-content: flex-end; gap: 8px; margin-top: 2px; }
</style>
